import React, { FC, useState } from 'react'
import { BaseFacetProps } from '../../types'
import styles from '../../Facets.module.scss'
import InputLabel from 'components/SA/FormElements/InputLabel'
import { PriceFacetConnectorProps } from 'connectors/Facets'
import { updateClientSideUrl } from '../../helpers/url'
import { getCurrencySymbol } from 'components/FormatPrice/helpers'

export type PriceObject = {
  priceMax: number | null
  priceMin: number | null
  priceError: boolean
}

const initialPriceState: PriceObject = {
  priceMax: null,
  priceMin: null,
  priceError: false,
}

type CustomPriceFieldsProps = BaseFacetProps & Partial<PriceFacetConnectorProps>

const CustomPriceFields: FC<CustomPriceFieldsProps> = ({
  baseUrl,
  nextAppRoute,
  priceMinMax,
  getHref,
  selectedCurrency,
}) => {
  let timer
  const timerDuration = 1500

  const [isCustomPriceOpen, setIsCustomPriceOpen] = useState(false)
  const [priceState, setPriceState] = useState<PriceObject>({
    ...initialPriceState,
    ...priceMinMax,
  })

  const { priceMin, priceMax, priceError } = priceState

  const handleCustomPriceToggle = () => {
    const { priceMin, priceMax } = priceState
    const shouldResetValue = !!priceMin || !!priceMax
    if (isCustomPriceOpen && shouldResetValue) {
      setPriceState(initialPriceState)
      const href = getHref?.({
        ['price']: '',
        page: '', // Reset Page
      })
      const sizeHref = `${baseUrl}${href}`
      updateClientSideUrl(sizeHref, nextAppRoute)
    }
    setIsCustomPriceOpen((prevValue) => !prevValue)
  }

  const validateNumber = (value: string): number => {
    const validNumber = Number(value)
    return isNaN(validNumber) || validNumber < 0 ? 0 : validNumber
  }

  const validatePrice = ({
    priceMin,
    priceMax,
  }: {
    priceMin: number | null
    priceMax: number | null
  }): boolean => // Valid if NO Min & Max || Min & Min=0, Max, Min < Max
    (priceMin === null && priceMax === null) ||
    !priceMin ||
    (!!priceMin && !!priceMax && priceMin <= priceMax)

  const inputUpdate = (data) => {
    clearTimeout(timer)
    const newPriceState = { ...priceState, ...data }
    const error = !validatePrice(newPriceState)
    setPriceState((prevValues) => ({ ...prevValues, ...data, priceError: error }))

    if (!error) {
      timer = setTimeout(() => {
        if (window && window.dataLayer) {
          window.dataLayer.push({
            event: 'filter',
            label: `Price - Custom - ${newPriceState.priceMin || ''}-${newPriceState.priceMax ||
              ''}`,
          })
        }

        const href = getHref?.({
          price: `${newPriceState.priceMin}-${newPriceState.priceMax}`,
          page: '', // Reset Page
        })
        const sizeHref = `${baseUrl}${href}`
        updateClientSideUrl(sizeHref, nextAppRoute)
      }, timerDuration)
    }

    return timer
  }

  return (
    <div className={styles.customSizeContainer}>
      <button onClick={handleCustomPriceToggle}>
        {isCustomPriceOpen ? 'Remove Custom Price' : 'Select Custom Price'}
      </button>
      {isCustomPriceOpen && (
        <div className={styles.sizeInputWrapper}>
          <div className={styles.sizeInputContainer}>
            <InputLabel
              type='text'
              name='priceMin'
              title={`${getCurrencySymbol(selectedCurrency)} Min`}
              value={priceMin || priceMin === 0 ? priceMin : ''}
              onChange={(ev) =>
                inputUpdate({
                  priceMin: validateNumber(ev.target.value),
                })
              }
              error={priceError ? ' ' : undefined}
            />
            {'-'}
            <InputLabel
              type='text'
              name='priceMax'
              title={`${getCurrencySymbol(selectedCurrency)} Max`}
              value={priceMax || ''}
              onChange={(ev) =>
                inputUpdate({
                  priceMax: validateNumber(ev.target.value),
                })
              }
              error={priceError ? ' ' : undefined}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomPriceFields
